import { OrderType, PickupTimeType, ServiceType } from "@/constants/enum";
import { DayPart } from "@/dto/dayPartMenu";
import { OrderEstimates } from "@/dto/orderEstimates";
import { Service } from "@/dto/service";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import {
  isWithinBusinessHours,
  isOrderSchedulingEnabled,
} from "@/utils/scheduleOrderUtils";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { SessionStorageKey } from "@/utils/storageKeys";
import { TIME_CONSTANTS } from "@/utils/constants";
import { getEffectiveLeadTime, toBusyModeFields } from "@/utils/busyModeLeadTime";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { DateTime } from "luxon";
import { useTranslations } from "next-intl";
import ChevronDownIcon from "../_icons/chevronDownIcon";

export const ScheduledOrderDropdown = ({
  scheduledOrder,
  setScheduledOrder,
  showScheduleScreen,
  merchant,
  orderType,
  selectedDayPart = null,
}: {
  scheduledOrder: any;
  setScheduledOrder: any;
  showScheduleScreen: () => void;
  merchant: any;
  orderType: string;
  selectedDayPart?: DayPart | null;
}) => {
  const t = useTranslations("ScheduledOrderDropdown");
  const tCart = useTranslations("RetailCartDrawer");
  const now = new Date();
  const nowMillis = now.getTime();
  const isOpen = isOrderSchedulingEnabled(merchant) && isBusinessOpen(merchant);
  const currentDateTime = DateTime.fromJSDate(now, {
    zone: merchant.timezone,
  });
  const merchantService = merchant?.services.find(
    (s: Service) =>
      s.type === ServiceType.PICKUP || s.type === ServiceType.WEBSITE
  );
  const businessHours = merchantService?.hours;
  const { deliveryTime, pickupTime } = getFromSessionStorage<
    Partial<OrderEstimates>
  >(SessionStorageKey.ORDER_ESTIMATES, {});

  const pickUpTimeDate = pickupTime ? new Date(pickupTime).getTime() : 0;
  const deliveryTimeDate = deliveryTime ? new Date(deliveryTime).getTime() : 0;
  const asapLeadTime = getEffectiveLeadTime(merchant.leadTime, toBusyModeFields(merchant), nowMillis)

  const deliveryPickupTimeText = Math.round(
    (pickUpTimeDate +
      asapLeadTime * TIME_CONSTANTS.MILLISECONDS_PER_MINUTE -
      nowMillis) /
      TIME_CONSTANTS.MILLISECONDS_PER_SECOND /
      TIME_CONSTANTS.MINUTES_PER_HOUR
  );

  const deliveryTimeText = Math.round(
    (deliveryTimeDate +
      asapLeadTime * TIME_CONSTANTS.MILLISECONDS_PER_MINUTE -
      nowMillis) /
      TIME_CONSTANTS.MILLISECONDS_PER_SECOND /
      TIME_CONSTANTS.MINUTES_PER_HOUR
  );

  const asapTime =
    deliveryTime && orderType === OrderType.DELIVERY
      ? t("deliverASAP", {
          from: deliveryPickupTimeText,
          to: deliveryTimeText,
        })
      : t("pickupASAP", { leadTime: asapLeadTime });

  const canShowASAP = isWithinBusinessHours(
    currentDateTime,
    businessHours,
    merchant?.timezone,
    selectedDayPart
  );

  const setAsapOrder = () => {
    saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
      orderType: orderType,
      asap: true,
      date: {
        displayValue: tCart("today"),
        dateObject: new Date(),
      },
      time: {
        displayValue: "",
        timeObject: new Date(now),
      },
    });
    setScheduledOrder({
      type: PickupTimeType.ASAP,
      epoch: currentDateTime.toMillis(),
    });
  };

  return (
    <Listbox
      value={scheduledOrder}
      onChange={(value) => {
        if (value.type === PickupTimeType.ASAP) {
          setAsapOrder();
        } else {
          showScheduleScreen();
        }
      }}
    >
      {({ open }) => (
        <div className="relative w-full">
          <ListboxButton
            className="relative w-full leading-6 text-gray-900 h-full hover:bg-gray-50 disabled:hover:bg-transparent disabled:cursor-default text-left"
            disabled={false}
            aria-label={`${t("forWhen")} ${
              scheduledOrder?.type === PickupTimeType.ASAP
                ? asapTime
                : scheduledOrder
            }`}
          >
            <div
              id="scheduled_order_input"
              className="cursor-pointer peer text-[15px] leading-6 block w-full min-h-[52px] h-full rounded-[4px] border border-black bg-transparent px-2 pt-6 pb-2 pr-8 text-gray-900 hover:border-black whitespace-normal break-words"
            >
              {scheduledOrder?.type === PickupTimeType.ASAP
                ? asapTime
                : scheduledOrder}
            </div>

            <span className="pointer-events-none cursor-pointer text-xs leading-[18px] absolute left-2 top-4 origin-[0] -translate-y-2.5 text-[#727272] tracking-wide">
              {t("forWhen")}
            </span>

            <ChevronDownIcon
              aria-hidden="true"
              className={`pointer-events-none cursor-pointer absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 transition-transform duration-300 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          </ListboxButton>
          <ListboxOptions
            transition
            className="absolute top-full z-20 w-full overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
          >
            <div className="p-3">
              {canShowASAP && (
                <ListboxOption
                  value={{
                    type: PickupTimeType.ASAP,
                    epoch: currentDateTime.toMillis(),
                  }}
                  className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50 data-[focus]:bg-gray-50"
                  aria-label={asapTime}
                >
                  <div className="flex-auto">
                    <p className="text-[15px]">{asapTime}</p>
                  </div>
                </ListboxOption>
              )}
              {isOpen && (
                <ListboxOption
                  value={{ type: PickupTimeType.SCHEDULED }}
                  className="group relative flex w-full items-center gap-x-6 rounded-lg p-3 leading-6 hover:bg-gray-50 data-[focus]:bg-gray-50"
                  aria-label={t("scheduleOrder")}
                >
                  <div className="flex-auto">
                    <p className="text-[15px]">{t("scheduleOrder")}</p>
                  </div>
                </ListboxOption>
              )}
            </div>
          </ListboxOptions>
        </div>
      )}
    </Listbox>
  );
};
